/*
 * Copyright (c) Nexign, JSC, 2022-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.19.0
 */

import {
    Fragment,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { CSSProperties, ReactNode } from 'react';

import classNames from 'classnames';
import get from 'lodash/get';
import InfiniteScroll from 'react-infinite-scroller';
import { Row, useBlockLayout, useExpanded, useResizeColumns, useTable } from 'react-table';
import { useSticky } from 'react-table-sticky';
import { VariableSizeList } from 'react-window';

import { getIsFastActionPage } from '@/main/components/Table/utils/tableUtils';
import { IAttrStyled } from '@/main/types/globalTypes';
import { table } from '@theme/index';

import { TABLE_WIDTH_MAX, TABLE_WIDTH_MIN } from '../constants';
import { useResizeObserver } from '../hooks/useTableResizer';
import { TTableColumnCell } from '../types';
import { TableLoaderStyled, TableStyled } from './styled/TableStyled';
import TableFooter from './TableFooter';
import TableNoData from './TableNoData';
import TableTooltip from './TableTooltip';

const headerProps = (props: any) => getHeaderStyles(props);

const getHeaderStyles = (props: any, align = 'left') => [
    props,
    {
        style: {
            justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
            alignItems: 'flex-start',
            display: 'flex',
        },
    },
];

interface ITableProps<Data extends object> extends Required<Pick<IAttrStyled, 'dataName'>> {
    data: Data[];
    columns: Array<TTableColumnCell<any>>;
    isLoading: boolean;
    isError: boolean;
    footer?: (HorScroll: ReactNode) => ReactNode;
    // size
    isCompact?: boolean;
    // По умолчанию таблица занимает высоту window.
    // Если установлен параметр containerHeight, то события scroll привязываются к table-body
    containerHeight?: number;
    // resize
    onResize?: (columnId: string, columnSize: number) => void;
    // infinite scroll
    infiniteScroll: {
        hasMore: boolean;
        initialLoad: boolean;
        pageStart: number;
        loadMore: (page: number) => void;
    };
    // data-attr for row, should be the key of data
    dataNameRow: string;
    // выставляет высоту ячеек по контенту
    isRowHeightAuto?: boolean;
    isInnerHtmlAvailable?: boolean;
    isInnerTable?: boolean;
}

const defaultColumn = { width: 220, maxWidth: TABLE_WIDTH_MAX, minWidth: TABLE_WIDTH_MIN };

const Table = <Data extends object>({
    columns,
    containerHeight,
    data,
    dataName,
    dataNameRow,
    footer,
    infiniteScroll,
    isCompact,
    isError,
    isLoading,
    isRowHeightAuto,
    onResize,
    isInnerHtmlAvailable,
    isInnerTable = false,
}: ITableProps<Data>) => {

    const virtualRef = useRef<VariableSizeList>(null);
    const tableBodyRef = useRef<HTMLDivElement>(null);
    const [isTableWasLoaded, setTableWasLoaded] = useState(false);

    const [isTableError, setTableError] = useState(isError);

    const tableInstance = useTable<any>(
        {
            columns,
            data,
            defaultColumn,
        },
        useResizeColumns,
        useBlockLayout,
        useExpanded,
        useSticky,
    );

    const {
        getTableProps,
        headerGroups,
        rows,
        prepareRow,
        totalColumnsWidth,
        state,
    } = tableInstance;

    const isFastActionPage = getIsFastActionPage();

    const rowPadding = useMemo(() => isCompact ? table.densityRowPadding : table.defaultRowPadding,
        [isCompact]);

    const rowHeight = useMemo(() => ({
        default: table.defaultRowHeight + 2 * rowPadding,
        defaultDouble: table.doubleRowHeight + 2 * rowPadding,
        next: table.nextRowHeight,
    }), [rowPadding]);

    const getRowSize = (index: number) => {
        let height: any;
        const row: Row<any> = rows[index];
        prepareRow(row);

        const labels = row.original.labels ?? [];

        if (labels.length > 1) {
            height = rowHeight.default + (labels.length - 1) * rowHeight.next;
        } else {
            // @ts-ignore
            const doubleRow = row.cells.some(cell => ((cell.column?.rowCount) === 2));
            height = doubleRow ? rowHeight.defaultDouble : rowHeight.default;
        }

        return height;
    };

    const RenderRow = useCallback(({ index, style }: { index: number; style: CSSProperties }) => {
            const row: Row<any> = rows[index];

            const checked = isFastActionPage ? row.original.fastActionChecked : row.original.checked;

            const rowClassName = classNames('rt-tr', {
                'rt-tr-checked': checked ?? false,
            });

            prepareRow(row);

        const dataAttr = get(row.original, dataNameRow, '');

            return (
                <div className='rt-tr-group'>
                    <div {...row.getRowProps({ style })} className={rowClassName} data-row-name={dataAttr}>
                        {row.cells.map((cell) => cell.render('Cell'))}
                    </div>
                </div>
            );
        },
        [prepareRow, rows, dataNameRow, isFastActionPage],
    );

    useResizeObserver(state, (columnId, columnSize) => {
        onResize?.(columnId, columnSize);
    });

    useEffect(() => {
        virtualRef.current?.resetAfterIndex(0, true);
    }, [isCompact, data, columns.length]);

    useEffect(() => {
        if (containerHeight) return
        window.addEventListener('scroll', windowScrollHandler, true);
        return () => {
            if (containerHeight) return
            window.removeEventListener('scroll', windowScrollHandler, true);
        };
    }, [containerHeight]);

    useEffect(() => {
        if (isLoading && !isTableWasLoaded) {
            setTableWasLoaded(true);
        }
    }, [isLoading, isTableWasLoaded]);

    useEffect(() => {
        setTableError(isError);
    }, [isError]);

    const windowScrollHandler = (): void => {
        const tableBodyScrollTop = tableBodyRef?.current?.getBoundingClientRect().top ?? 0;
        if (tableBodyScrollTop < 0) {
            virtualRef.current?.scrollTo(Math.abs(tableBodyScrollTop));
        } else {
            virtualRef.current?.scrollTo(0);
        }
    };

    const tableRef = useRef<HTMLDivElement>(null);

    /*
    * По умолчанию таблица занимает высоту window.
    * Если установлен параметр containerHeight, то события scroll привязываются к table-body
    */

    const tableBodyStyle = containerHeight ? { maxHeight: containerHeight, overflow: 'auto' } : undefined

    const virtualizeContainerHeight = containerHeight
        ? (tableBodyRef.current?.scrollHeight ?? window.innerHeight)
        : window.innerHeight;

    const getVirtualScrollParent = containerHeight
        ? () => tableBodyRef.current
        : undefined
    const virtualUseWindow = !containerHeight;

    return (
        <TableStyled isInnerTable={isInnerTable} rowPadding={rowPadding}>
            <div
                {...getTableProps()}
                className={classNames('sticky rt-table', { 'compact': isCompact })}
                data-name={dataName}
                data-table-name={dataName}
                ref={tableRef}
            >
                <div className="rt-thead -header">
                    {headerGroups.map(headerGroup => (
                        <div
                            {...headerGroup.getHeaderGroupProps({
                            })}
                            className="tr rt-tr"
                        >
                            {headerGroup.headers.map(column => (
                                <div
                                    {...column.getHeaderProps(headerProps)}
                                    className="th rt-th"
                                    data-col-name={column.id}
                                    data-name={column.id}
                                >
                                    {column.render('Header')}
                                    {/* Use column.getResizerProps to hook up the events correctly */}
                                    {
                                        // @ts-ignore
                                        column.canResize && (
                                            <div
                                                {
                                                // @ts-ignore
                                                ...column.getResizerProps()
                                                }
                                                className={`resizer rt-resizer ${
                                                    // @ts-ignore
                                                    column.isResizing ? 'isResizing' : ''
                                                    }`}
                                            />
                                        )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                {isLoading && <TableLoaderStyled style={{ top: 73 }} />}
                <div className="tbody rt-tbody" ref={tableBodyRef} style={tableBodyStyle}>
                    <InfiniteScroll
                        {...infiniteScroll}
                        getScrollParent={getVirtualScrollParent}
                        useWindow={virtualUseWindow}
                    >
                        <Fragment>
                            {
                                /*
                                 * Для оптимизации рендера ячеек используется VariableSizeList
                                 * Для каждой ячейки необходимо задать фиксированную высоту. @see getRowSize
                                 * Если высота ячейки указана как auto, то используется стандартный рендер
                                 */

                                isRowHeightAuto
                                    ? data.map((_, index) => RenderRow({ index, style: {} }))
                                    : <VariableSizeList
                                        className="window-scroller-override"
                                        height={virtualizeContainerHeight}
                                        itemCount={isTableError ? 0 : rows.length}
                                        itemSize={(index) => getRowSize(index)}
                                        ref={virtualRef}
                                        width={totalColumnsWidth}
                                    >
                                        {RenderRow}
                                    </VariableSizeList>
                            }
                        </Fragment>
                    </InfiniteScroll>
                </div>
            </div>
            {
                ((!!data && data.length === 0 && !isLoading) || isTableError) &&
                <TableNoData isDataLoaded={isTableWasLoaded} isError={isTableError} />
            }
            <TableTooltip isInnerHtmlAvailable={isInnerHtmlAvailable} tableRef={tableRef} />
            {tableRef.current && (
                <TableFooter
                    footer={footer}
                    key={tableRef.current?.clientWidth}
                    tableRef={tableRef}
                    totalColumnsWidth={totalColumnsWidth}
                />
            )}
        </TableStyled>
    );
};

export default memo(Table);
